import {
  ChartBarIcon as StatsIcon,
  LinkIcon as ChainIcon,
  LockClosedIcon as WhirlpoolIcon,
  RefreshIcon,
  SparklesIcon as PolywaveIcon,
  SunIcon as BeachIcon,
} from '@heroicons/react/outline';
import { AumChain } from 'components/Aum';
import { Card, ChainCards, CurrencyToggleCard } from 'components/Card';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { PolygonTreasuryTotal } from 'components/PolygonTreasury';
import { Section } from 'components/Section';
import { useChain, usePrice, useStats } from 'contexts';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCount, formatPercent } from 'utils/number';
import { ChainId, Currency } from 'utils/types';

const GlobalStats = () => {
  const { t } = useTranslation();
  const { id: chainId, shortDisplayName } = useChain();
  const { getChainTokenPrices } = usePrice();
  const {
    currentStats: {
      totalSupply,
      totalBurnt = 0,
      totalLocked,
      lpPrice,
      lpTokensLocked = 0,
      lpTokensTotal,
      tokenReserve,
      otherReserve,
      whirlpoolBalance,
      whirlpoolTotalStaked,
      towelTotalSupply,
      towelTotalStaked,
    },
  } = useStats();

  const tokenPrices = useMemo(() => getChainTokenPrices(chainId), [chainId, getChainTokenPrices]);
  const ethUsd = tokenPrices[Currency.ETH]?.[Currency.USD];

  return (
    <div className="container">
      <Section title={t('home.section_header_chains')} icon={ChainIcon}>
        <ChainCards />
      </Section>

      <Section
        title={t('home.section_header_stats', { chain: shortDisplayName })}
        icon={StatsIcon}
        className="mt-10"
      >
        <AumChain shortDisplayName={shortDisplayName} />

        {chainId === ChainId.MaticMainnet && <PolygonTreasuryTotal />}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
          {!!totalSupply && (
            <CurrencyToggleCard
              title={t('global_stats.market_cap')}
              chainId={chainId}
              amount={totalSupply}
              defaultQuoteCurrency={Currency.USD}
              hideSurfOption
            />
          )}
          <CurrencyToggleCard title={t('global_stats.token_price')} chainId={chainId} amount={1} />
          {!!totalSupply && (
            <Card
              title={t('global_stats.total_supply')}
              body={
                <>
                  {formatCount(totalSupply - totalBurnt)}
                  <span className="pl-1 text-xl text-gray-999">SURF</span>
                </>
              }
              footer={
                !!totalLocked && (
                  <div className="uppercase">
                    {t('global_stats.tokens_circulating', {
                      total: formatCount(totalSupply - totalLocked),
                    })}
                  </div>
                )
              }
            />
          )}
          {!!lpPrice && (
            <>
              <CurrencyToggleCard
                title={t('global_stats.lp_token_price')}
                chainId={chainId}
                amount={lpPrice}
                baseCurrency={Currency.ETH}
                defaultQuoteCurrency={Currency.USD}
              />
              <Card
                title={t(
                  chainId === ChainId.MaticMainnet
                    ? 'global_stats.quickswap_liquidity'
                    : 'global_stats.locked_liquidity'
                )}
                body={
                  <>
                    $
                    {ethUsd ? formatCount(lpPrice * lpTokensLocked * ethUsd) : <LoadingIndicator />}
                    <span className="pl-1 text-xl text-gray-999">USD</span>
                  </>
                }
                footer={
                  <>
                    {!!lpTokensLocked && !!lpTokensTotal && lpTokensLocked !== lpTokensTotal && (
                      <div>
                        {t('global_stats.lp_tokens', { tokensCount: formatCount(lpTokensLocked) })}{' '}
                        |{' '}
                        {t('global_stats.lp_percentage', {
                          tokensPercentage: `${((lpTokensLocked * 100) / lpTokensTotal).toFixed(
                            2
                          )}%`,
                        })}
                      </div>
                    )}
                    {!!tokenReserve && !!otherReserve && !!lpTokensTotal && (
                      <div>
                        ({formatCount((otherReserve * lpTokensLocked) / lpTokensTotal)} ETH +{' '}
                        {formatCount((tokenReserve * lpTokensLocked) / lpTokensTotal)} SURF)
                      </div>
                    )}
                  </>
                }
              />
            </>
          )}
        </div>
      </Section>

      {!!whirlpoolBalance && !!lpPrice && !!whirlpoolTotalStaked && (
        <Section
          title={t('global_stats.section_header_the_whirlpool')}
          icon={WhirlpoolIcon}
          className="mt-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
            <CurrencyToggleCard
              title={t('global_stats.whirlpool_balance')}
              chainId={chainId}
              amount={whirlpoolBalance}
              defaultQuoteCurrency={Currency.SURF}
            />

            <Card
              title={t('global_stats.whirlpool_total_staked')}
              body={
                <>
                  $
                  {ethUsd ? (
                    formatCount(lpPrice * whirlpoolTotalStaked * ethUsd)
                  ) : (
                    <LoadingIndicator />
                  )}
                  <span className="pl-1 text-xl text-gray-999">USD</span>
                </>
              }
              footer={
                !!whirlpoolTotalStaked && (
                  <>
                    {!!lpTokensLocked && !!lpTokensTotal && (
                      <div>
                        {t('global_stats.lp_tokens', {
                          tokensCount: formatCount(whirlpoolTotalStaked),
                        })}{' '}
                        |{' '}
                        {t('global_stats.lp_percentage', {
                          tokensPercentage: formatPercent(whirlpoolTotalStaked / lpTokensTotal),
                        })}
                      </div>
                    )}
                    {!!tokenReserve && !!otherReserve && !!lpTokensTotal && (
                      <div>
                        ({formatCount((otherReserve * whirlpoolTotalStaked) / lpTokensTotal)} ETH +{' '}
                        {formatCount((tokenReserve * whirlpoolTotalStaked) / lpTokensTotal)} SURF)
                      </div>
                    )}
                  </>
                )
              }
            />
          </div>
        </Section>
      )}

      {(tokenPrices[Currency.WLEV] || tokenPrices[Currency.RSURF]) && (
        <Section
          title={t('global_stats.section_header_wrapped', { chain: shortDisplayName })}
          icon={RefreshIcon}
          className="mt-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
            {tokenPrices[Currency.WLEV] && (
              <CurrencyToggleCard
                title={t('global_stats.wlev_price')}
                chainId={chainId}
                amount={1}
                baseCurrency={Currency.WLEV}
                defaultQuoteCurrency={Currency.USD}
              />
            )}
            {tokenPrices[Currency.RSURF] && (
              <CurrencyToggleCard
                title={t('global_stats.rsurf_price')}
                chainId={chainId}
                amount={1}
                baseCurrency={Currency.RSURF}
                defaultQuoteCurrency={Currency.USD}
              />
            )}
          </div>
        </Section>
      )}

      {tokenPrices[Currency.TOWEL] && !!towelTotalSupply && !!towelTotalStaked && (
        <Section
          title={t('global_stats.section_header_the_beach')}
          icon={BeachIcon}
          className="mt-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
            <CurrencyToggleCard
              title={t('global_stats.towel_price')}
              chainId={chainId}
              amount={1}
              baseCurrency={Currency.TOWEL}
              defaultQuoteCurrency={Currency.USD}
            />
            <Card
              title={t('global_stats.towel_total_supply')}
              body={formatCount(towelTotalSupply)}
            />
            <Card
              title={t('global_stats.towel_total_staked')}
              body={formatCount(towelTotalStaked)}
            />
          </div>
        </Section>
      )}

      {tokenPrices[Currency.WAVE] && (
        <Section
          title={t('global_stats.section_header_polywave')}
          icon={PolywaveIcon}
          className="mt-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
            <CurrencyToggleCard
              title={t('global_stats.wave_price')}
              chainId={chainId}
              amount={1}
              baseCurrency={Currency.WAVE}
              defaultQuoteCurrency={Currency.USD}
            />
            <CurrencyToggleCard
              title={t('global_stats.wave_matic_lp_price')}
              chainId={chainId}
              amount={1}
              baseCurrency={Currency.WAVE_MATIC_LP}
              defaultQuoteCurrency={Currency.USD}
            />
          </div>
        </Section>
      )}
    </div>
  );
};

export default GlobalStats;
